import React from 'react';
import { motion } from 'framer-motion';

const WhatIsVBC = () => {
  const focusAreas = [
    {
      id: 'investments',
      title: 'Strategic Investments',
      description: 'We identify and invest in high-potential startups and scale-ups, providing capital and strategic guidance to accelerate growth and market impact. Areas of investment include Fintech, Deeptech, Healthtech, Web 3/Crypto, Real Estate.',
      image: 'https://images.pexels.com/photos/7789849/pexels-photo-7789849.jpeg',
      stats: '',
      features: [
        'Early stage funding, Angel, Pre-Seed to Series B',
        'Fund LP investments',
        'Growth capital',
        'Strategic Partnership Capital, JVs, Secondaries, Pre-IPO opportunities',
        'Venture Debt & Debt syndication'
      ]
    },
    {
      id: 'venture-building',
      title: 'Venture Building & Advisory',
      description: 'From concept to market launch, we build ventures from the ground up and provide strategic advisory services, leveraging our expertise, network, and resources to create successful businesses.',
      image: 'https://images.unsplash.com/photo-1490351267196-b7a67e26e41b',
      stats: '',
      features: [
        'Concept development',
        'Strategic Hiring, Fractional CXO interim support',
        'Product development',
        'Strategic planning',
        'Go-to-market strategy',
        'Corporate development',
        'Legal, Compliance & Regulatory Support'
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="what-is-vbc" className="py-24 bg-gradient-to-b from-navy-950 to-navy-900 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%2306b6d4' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <h2 className="text-display font-bold text-white mb-6">
            What is <span className="gradient-text">VBC</span>?
          </h2>
          <p className="text-xl text-gray-300 leading-relaxed">
            Venture Builders Collective operates through strategic investments and comprehensive venture building 
            services that drive innovation and entrepreneurial success.
          </p>
        </motion.div>

        {/* Focus Areas Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto"
        >
          {focusAreas.map((area, index) => (
            <motion.div
              key={area.id}
              variants={cardVariants}
              className="group relative"
            >
              <div className="glass rounded-2xl p-8 h-full transform transition-all duration-500 hover:scale-105 hover:shadow-2xl border border-white/10">
                {/* Image */}
                <div className="relative overflow-hidden rounded-xl mb-6">
                  <img
                    src={area.image}
                    alt={area.title}
                    className="w-full h-48 object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 to-transparent" />
                  {area.stats && (
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="text-secondary-400 font-semibold text-sm">
                        {area.stats}
                      </div>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${
                      index === 0 ? 'from-secondary-400 to-primary-400' :
                      'from-primary-400 to-accent-400'
                    }`} />
                    <h3 className="text-xl font-bold text-white group-hover:text-secondary-400 transition-colors duration-300">
                      {area.title}
                    </h3>
                  </div>

                  <p className="text-gray-300 text-sm leading-relaxed">
                    {area.description}
                  </p>

                  {/* Features List */}
                  <div className="grid grid-cols-1 gap-2 pt-4">
                    {area.features.map((feature, featureIndex) => (
                      <motion.div
                        key={featureIndex}
                        initial={{ opacity: 0, x: -10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 + featureIndex * 0.1 }}
                        viewport={{ once: true }}
                        className="flex items-center space-x-2 text-sm text-gray-400"
                      >
                        <div className="w-1.5 h-1.5 bg-secondary-400 rounded-full" />
                        <span>{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Hover Effect Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-secondary-500/5 to-primary-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <p className="text-lg text-gray-300 mb-8">
            Ready to transform your innovative ideas into successful ventures?
          </p>
          <button
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            className="btn-primary"
          >
            Start Your Journey
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default WhatIsVBC;